import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Volume2, VolumeX, Cat, Sparkles } from "lucide-react";
import useAudio from "./useAudio";
import { MOODS, WIN_AFFECTION, WIN_AMBUSHES } from "./muskiData";
import MuskiStage from "./components/MuskiStage";
import AffectionMeter from "./components/AffectionMeter";
import ActionDeck from "./components/ActionDeck";
import BioCard from "./components/BioCard";
import RugTrap from "./components/RugTrap";
import VictoryModal from "./components/VictoryModal";
import HallOfFame from "./components/HallOfFame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const clamp = (n) => Math.max(0, Math.min(100, n));

export default function MuskiGame() {
  const { sfx, muted, setMuted } = useAudio();
  const [bio, setBio] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [playerName, setPlayerName] = useState("");

  const [mood, setMood] = useState("Calm");
  const [affection, setAffection] = useState(20);
  const [ambushes, setAmbushes] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [shaking, setShaking] = useState(false);
  const [rugOpen, setRugOpen] = useState(false);
  const [victory, setVictory] = useState(false);
  const [duration, setDuration] = useState(0);

  const relaxRef = useRef(null);
  const startRef = useRef(Date.now());
  const savedRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/muski`).then((r) => setBio(r.data)).catch(() => {});
    refreshBoard();
  }, []);

  const refreshBoard = () =>
    axios.get(`${API}/leaderboard?limit=8`).then((r) => setLeaderboard(r.data)).catch(() => {});

  const addReaction = useCallback((text, type) => {
    const id = Math.random().toString(36).slice(2);
    const x = 30 + Math.random() * 40;
    setReactions((p) => [...p, { id, text, type, x }]);
    setTimeout(() => setReactions((p) => p.filter((r) => r.id !== id)), 1100);
  }, []);

  const bump = useCallback((delta) => setAffection((a) => clamp(a + delta)), []);

  const flinch = useCallback(() => {
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  }, []);

  const relaxLater = useCallback(() => {
    clearTimeout(relaxRef.current);
    relaxRef.current = setTimeout(() => setMood("Calm"), 3800);
  }, []);

  // win detection
  useEffect(() => {
    if (affection >= WIN_AFFECTION && ambushes >= WIN_AMBUSHES && !victory) {
      setVictory(true);
      setMood("Calm");
      const secs = Math.round((Date.now() - startRef.current) / 1000);
      setDuration(secs);
      sfx.win();
      if (!savedRef.current) {
        savedRef.current = true;
        axios
          .post(`${API}/sessions`, {
            player_name: playerName.trim() || "Anonymous",
            affection,
            ambushes_survived: ambushes,
            won: true,
            duration_seconds: secs,
          })
          .then(refreshBoard)
          .catch(() => {});
      }
    }
  }, [affection, ambushes, victory, playerName, sfx]);

  const handleAction = (key) => {
    if (key === "rug") {
      setMood("Ambush");
      setRugOpen(true);
      return;
    }
    if (key === "pet") {
      if (mood === "Ambush") {
        sfx.hiss();
        addReaction("HISSS!", "bad");
        flinch();
        bump(-9);
        return;
      }
      sfx.purr();
      addReaction("purrrr", "love");
      bump(6);
      setMood("Calm");
      relaxLater();
      return;
    }
    if (key === "feed") {
      sfx.pop();
      addReaction("nom nom!", "gold");
      bump(11);
      setMood("Curious");
      relaxLater();
      return;
    }
    if (key === "toy") {
      sfx.meow();
      addReaction("boing!", "zoom");
      bump(8);
      setMood("Zoomies");
      relaxLater();
      return;
    }
  };

  const handleRugResult = (kind) => {
    if (kind === "survived") {
      sfx.pounce();
      setAmbushes((a) => Math.min(WIN_AMBUSHES, a + 1));
      bump(13);
      addReaction("SURVIVED!", "good");
      setMood("Zoomies");
    } else if (kind === "early") {
      sfx.hiss();
      bump(-10);
      flinch();
      addReaction("TOO SOON!", "bad");
    } else {
      sfx.pounce();
      bump(-5);
      flinch();
      addReaction("AMBUSHED!", "bad");
    }
    relaxLater();
  };

  const replay = () => {
    setVictory(false);
    setAffection(20);
    setAmbushes(0);
    setMood("Calm");
    setReactions([]);
    savedRef.current = false;
    startRef.current = Date.now();
  };

  return (
    <div className="muski-page" data-testid="muski-game">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* header */}
        <header className="flex flex-col gap-3 border-b border-slate-800 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-500/15 ring-1 ring-sky-400/40">
              <Cat className="h-6 w-6 text-sky-300" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-black tracking-tight text-white">
                Play With <span className="text-sky-400">Muski</span>
              </h1>
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-slate-500">
                Get the purr · survive the ambush
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="player-name-input"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Your name"
              maxLength={40}
              className="rounded-xl bg-slate-900/70 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-400 focus:outline-none"
              style={{ transition: "border-color 0.2s ease" }}
            />
            <button
              data-testid="mute-toggle"
              onClick={() => setMuted((m) => !m)}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900/70 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500"
              style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
              aria-label="toggle sound"
            >
              {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
          </div>
        </header>

        {/* body grid */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <section className="flex flex-col gap-6 lg:col-span-8">
            <MuskiStage mood={mood} tagline={MOODS[mood].tagline} reactions={reactions} shaking={shaking} />
            <div>
              <div className="mb-3 flex items-center gap-2 text-slate-400">
                <Sparkles className="h-4 w-4 text-amber-400" />
                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em]">Interactions</span>
              </div>
              <ActionDeck onAction={handleAction} disabled={rugOpen || victory} />
            </div>
          </section>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <AffectionMeter affection={affection} ambushes={ambushes} />
            <HallOfFame entries={leaderboard} />
          </aside>

          <div className="lg:col-span-12">
            <BioCard bio={bio} />
          </div>
        </div>

        <footer className="mt-8 border-t border-slate-800 pt-5 text-center">
          <p className="font-royal italic text-slate-500">
            Muski: royal face, blue-eyed stare, zero impulse control. Approach the rug at your own risk.
          </p>
        </footer>
      </div>

      <RugTrap open={rugOpen} onClose={() => setRugOpen(false)} onResult={handleRugResult} />
      <VictoryModal open={victory} affection={affection} ambushes={ambushes} duration={duration} onReplay={replay} />
    </div>
  );
}
